import { Button, Input, Select } from "@/components";
import { useTranslation } from "@/contexts/LanguageContext";
import apiService from "@/shared/apiService";
import {
  CheckCircle,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Smartphone,
  Target,
  Users,
  UserSearch,
  X,
  Zap,
} from "lucide-react";
import React from "react";
import { useAds } from "../../hooks/AdsContext";

const LinkedInTargetingSearch: React.FC<{
  facet: string;
  placeholder: string;
  selectedItems: Array<{ urn: string; name: string }>;
  onAdd: (item: { urn: string; name: string }) => void;
  onRemove: (urn: string) => void;
}> = ({ facet, placeholder, selectedItems, onAdd, onRemove }) => {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState("");
  const [suggestions, setSuggestions] = React.useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const search = async (q: string) => {
    if (q.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setLoading(true);
    try {
      const data = await apiService.get("/api/ads/linkedin/targeting/search", {
        facet,
        q,
      });
      if (data.success) {
        setSuggestions(data.elements);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      console.error("LinkedIn search error:", err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(value), 300);
  };

  return (
    <div className="group/search-li" ref={containerRef}>
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0077b5]" />
        <input
          placeholder={placeholder}
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true);
          }}
          className="w-full h-10 pl-10 pr-9 bg-[#0077b5]/5 border border-[#0077b5]/10 rounded-xl text-xs font-medium outline-hidden focus:ring-2 focus:ring-[#0077b5]/20 focus:border-[#0077b5] transition-all placeholder:text-[#0077b5]/40"
        />
        {loading && (
          <div className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin rounded-full h-3 w-3 border-2 border-[#0077b5] border-t-transparent" />
        )}

        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-blue-50 rounded-xl shadow-xl z-60 max-h-[200px] overflow-y-auto">
            {suggestions.map((s, idx) => {
              const isSelected = !!selectedItems.find((i) => i.urn === s.urn);
              return (
                <button
                  key={idx}
                  type="button"
                  className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-2 border-b border-gray-50 last:border-0 group/item transition-colors ${isSelected ? "bg-[#0077b5]/10 cursor-default" : "hover:bg-[#0077b5]/5"}`}
                  onClick={() => {
                    if (!isSelected) {
                      onAdd({ urn: s.urn, name: s.name });
                      setQuery("");
                      setShowSuggestions(false);
                    }
                  }}
                >
                  <span
                    className={`text-xs font-semibold truncate flex-1 min-w-0 ${isSelected ? "text-[#0077b5]" : "text-gray-700"}`}
                  >
                    {s.name}
                  </span>
                  {isSelected ? (
                    <CheckCircle className="w-3.5 h-3.5 text-[#0077b5] shrink-0" />
                  ) : (
                    <Plus className="w-3.5 h-3.5 text-[#0077b5]/30 group-hover/item:text-[#0077b5] transition-colors shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {showSuggestions &&
          query.length >= 3 &&
          suggestions.length === 0 &&
          !loading && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-blue-50 rounded-xl shadow-xl z-60 px-4 py-3">
              <span className="text-[11px] text-gray-400 font-medium">
                {t('Nenhum resultado para "{query}"', { query })}
              </span>
            </div>
          )}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-2 min-h-[24px]">
        {selectedItems.map((item, idx) => (
          <span
            key={idx}
            className="inline-flex items-center gap-1.5 max-w-full px-2.5 py-1 bg-[#0077b5] text-white rounded-lg text-[10px] font-bold shadow-xs animate-in zoom-in-95"
            title={item.name}
          >
            <span className="truncate max-w-[180px] sm:max-w-[220px]">
              {item.name}
            </span>
            <button
              type="button"
              onClick={() => onRemove(item.urn)}
              className="text-white/60 hover:text-white transition-colors shrink-0 ml-0.5"
              aria-label={t("Remover {name}", { name: item.name })}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {selectedItems.length === 0 && (
          <span className="text-[10px] text-[#0077b5]/40 italic font-medium">
            {t("Nenhum selecionado")}
          </span>
        )}
      </div>
    </div>
  );
};

// URNs from the old local fallback catalog that map to wrong LinkedIn locations.
// Must be blocked on the frontend before they reach campaign creation.
const KNOWN_BAD_GEO_URNS = new Set([
  "urn:li:geo:100282103", // was "São Paulo, SP" → actually Kalaluwe (Africa/Sri Lanka)
  "urn:li:geo:101372551", // was "Rio de Janeiro, RJ"
  "urn:li:geo:100257008", // was "Belo Horizonte, MG"
  "urn:li:geo:100088825", // was "Curitiba, PR"
  "urn:li:geo:100142100", // was "Porto Alegre, RS"
  "urn:li:geo:100124803", // was "Brasília, DF"
  "urn:li:geo:100154481", // was "Salvador, BA"
  "urn:li:geo:100154482", // was "Fortaleza, CE"
  "urn:li:geo:100154483", // was "Manaus, AM"
  "urn:li:geo:100154484", // was "Recife, PE"
  "urn:li:geo:100154485", // was "Belém, PA"
  "urn:li:geo:100154486", // was "Goiânia, GO"
  "urn:li:geo:100154487", // was "Campinas, SP"
  "urn:li:geo:100154488", // was "Florianópolis, SC"
]);

const AudienceProfile: React.FC = () => {
  const {
    platform,
    ageMin,
    setAgeMin,
    ageMax,
    setAgeMax,
    gender,
    setGender,
    interests,
    setInterests,
    keywords,
    setKeywords,
    useMetaLookalike,
    setUseMetaLookalike,
    metaLookalikeSource,
    setMetaLookalikeSource,
    metaLookalikePercent,
    setMetaLookalikePercent,
    createMetaLookalike,
    creatingLookalike,
    useMetaCustomAudience,
    setUseMetaCustomAudience,
    metaCustomAudienceName,
    setMetaCustomAudienceName,
    metaCustomAudienceId,
    setMetaCustomAudienceId,
    metaCustomAudiencesList,
    fetchingCustomAudiences,
    fetchMetaCustomAudiences,
    createMetaCustomAudience,
    creatingCustomAudience,
    useGoogleCustomerMatch,
    setUseGoogleCustomerMatch,
    googleCustomerMatchSource,
    setGoogleCustomerMatchSource,
    createGoogleCustomerMatch,
    creatingCustomerMatch,
    optimizeKeywordsWithTrends,
    trendsLoading,
    linkedinAccounts,
    loadingLinkedinAccounts,
    selectedLinkedinAccount,
    setSelectedLinkedinAccount,
    linkedinJobTitles,
    setLinkedinJobTitles,
    linkedinIndustries,
    setLinkedinIndustries,
    linkedinSeniorities,
    setLinkedinSeniorities,
    linkedinLanguage,
    setLinkedinLanguage,
    customLocations,
    setCustomLocations,
  } = useAds();
  const { t } = useTranslation();

  const [interestSuggestions, setInterestSuggestions] = React.useState<any[]>(
    [],
  );
  const [searchingInterests, setSearchingInterests] = React.useState(false);
  const [interestQuery, setInterestQuery] = React.useState("");
  const [interestSearchError, setInterestSearchError] = React.useState(false);

  const addKeyword = (kw: string) => {
    if (!keywords.includes(kw)) setKeywords([...keywords, kw]);
  };

  const removeKeyword = (idx: number) => {
    setKeywords(keywords.filter((_, i) => i !== idx));
  };

  const {
    linkedinCompanySizes,
    setLinkedinCompanySizes,
    linkedinSkills,
    setLinkedinSkills,
    linkedinJobFunctions,
    setLinkedinJobFunctions,
    linkedinMatchedAudiences,
    setLinkedinMatchedAudiences,
  } = useAds();

  const [availableAudiences, setAvailableAudiences] = React.useState<
    Array<{ urn: string; name: string; type?: string; status?: string }>
  >([]);
  const [loadingAudiences, setLoadingAudiences] = React.useState(false);
  const [audiencesError, setAudiencesError] = React.useState(false);

  React.useEffect(() => {
    if (platform !== "linkedin") return;
    const bad = customLocations.filter(
      (l) => l.urn && KNOWN_BAD_GEO_URNS.has(l.urn),
    );
    if (bad.length > 0) {
      setCustomLocations(
        customLocations.filter((l) => !l.urn || !KNOWN_BAD_GEO_URNS.has(l.urn)),
      );
    }
  }, [platform, customLocations]);

  const fetchAvailableAudiences = React.useCallback(() => {
    if (platform !== "linkedin" || !selectedLinkedinAccount) return;
    setLoadingAudiences(true);
    setAudiencesError(false);
    apiService
      .get(
        `/api/ads/linkedin/audiences?account_urn=${encodeURIComponent(selectedLinkedinAccount)}`,
      )
      .then((res: any) => {
        const data = res.data || res;
        setAvailableAudiences(
          (data.audiences || data.elements || []).map((a: any) => ({
            urn: a.urn,
            name: a.name,
            type: a.type,
            status: a.status,
          })),
        );
      })
      .catch(() => {
        setAvailableAudiences([]);
        setAudiencesError(true);
      })
      .finally(() => setLoadingAudiences(false));
  }, [platform, selectedLinkedinAccount]);

  React.useEffect(() => {
    fetchAvailableAudiences();
  }, [fetchAvailableAudiences]);

  return (
    <div className="flex-1 flex flex-col bg-white rounded-2xl p-4 md:p-5 border border-purple-200/70 shadow-xs space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-3 border-purple-100">
        <div className="flex items-start gap-2.5">
          <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-purple-600 rounded-lg text-white shadow-xs">
            <Target className="w-5 h-5" />
          </div>
          <div className="h-8 flex flex-col justify-between">
            <h2 className="text-xs font-bold text-gray-900 uppercase tracking-tight leading-none">
              {t("Segmentação de Público")}
            </h2>
            <span className="text-[10px] text-gray-400 font-medium leading-none">
              {t("Demografia, interesses e públicos CRM")}
            </span>
          </div>
        </div>
      </div>

      {/* Main Grid: Demographics + Platform Targeting */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 items-stretch">
        {/* Demographics Column */}
        <div className="flex flex-col space-y-3 p-3.5 bg-purple-50/20 rounded-xl border border-purple-100/70">
          <div className="flex items-center gap-2 font-bold text-purple-700 text-xs uppercase tracking-tight border-b pb-2 border-purple-100/70">
            <UserSearch className="w-3.5 h-3.5" />
            {t("Demografia Base")}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input
              label={t("Idade Mín.")}
              type="number"
              value={ageMin}
              onChange={(e) => setAgeMin(e.target.value)}
              min="18"
              max="65"
              inputSize="sm"
              className="text-xs font-semibold rounded-xl border-purple-200"
            />
            <Input
              label={t("Idade Máx.")}
              type="number"
              value={ageMax}
              onChange={(e) => setAgeMax(e.target.value)}
              min="18"
              max="65"
              inputSize="sm"
              className="text-xs font-semibold rounded-xl border-purple-200"
            />
            <Select
              label={t("Gênero")}
              value={gender}
              onChange={(val) => setGender(val as any)}
              options={[
                { value: "all", label: t("Todos") },
                { value: "male", label: t("Masc.") },
                { value: "female", label: t("Fem.") },
              ]}
              size="sm"
              className="text-xs rounded-xl border-purple-200"
            />
          </div>

          {platform === "linkedin" && (
            <div className="space-y-1.5 pt-1">
              <label className="text-[10px] font-bold uppercase text-purple-700 tracking-wider">
                {t("Idioma do Perfil")}
              </label>
              <LinkedInTargetingSearch
                facet="interfaceLocales"
                placeholder={t("Ex: Português, Inglês...")}
                selectedItems={
                  linkedinLanguage
                    ? [
                        {
                          urn: linkedinLanguage,
                          name:
                            linkedinLanguage.split(":").pop() ||
                            linkedinLanguage,
                        },
                      ]
                    : []
                }
                onAdd={(item) => setLinkedinLanguage(item.urn)}
                onRemove={() => setLinkedinLanguage("")}
              />
            </div>
          )}
        </div>

        {/* Interests / Keywords Column */}
        <div className="flex flex-col space-y-3">
          {platform === "meta" ? (
            <div className="flex-1 flex flex-col p-3.5 bg-white rounded-xl border border-purple-100/80 space-y-2.5 shadow-xs">
              <div className="flex items-center gap-2 font-bold text-purple-700 text-xs uppercase tracking-tight border-b pb-2 border-purple-50">
                <Search className="w-3.5 h-3.5" />
                {t("Interesses na Meta")}
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase text-gray-500">
                  {t("Buscar Interesses")}
                </label>
                <div className="relative">
                  <Plus className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-600" />
                  <input
                    placeholder={t("Buscar interesses (mínimo 3 letras)...")}
                    className="w-full h-10 pl-10 pr-9 bg-purple-50/30 border border-purple-200 rounded-xl text-xs outline-hidden focus:border-purple-600 focus:ring-2 focus:ring-purple-500/20 transition-all placeholder:text-gray-400 font-medium"
                    onChange={async (e) => {
                      const query = e.target.value.trim();
                      setInterestQuery(query);
                      setInterestSearchError(false);
                      if (query.length < 3) {
                        setInterestSuggestions([]);
                        return;
                      }
                      setSearchingInterests(true);
                      try {
                        const res = await apiService.get(
                          `/api/ads/meta/search-detailed-targeting?type=adinterest&q=${encodeURIComponent(query)}`,
                        );
                        if (res.success && Array.isArray(res.data)) {
                          setInterestSuggestions(res.data);
                        } else {
                          setInterestSuggestions([]);
                          setInterestSearchError(true);
                        }
                      } catch (err) {
                        console.error("Erro ao buscar interesses Meta:", err);
                        setInterestSuggestions([]);
                        setInterestSearchError(true);
                      } finally {
                        setSearchingInterests(false);
                      }
                    }}
                  />
                  {searchingInterests && (
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />
                    </div>
                  )}
                </div>
              </div>

              {/* Suggestions dropdown */}
              {interestSuggestions.length > 0 && (
                <div className="max-h-36 overflow-y-auto border border-purple-100 rounded-xl bg-white p-1 space-y-0.5 shadow-md animate-in zoom-in-95 duration-150">
                  {interestSuggestions.map((item) => {
                    const isSelected = !!interests.find(
                      (i) => i.id === item.id,
                    );
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          if (!isSelected) {
                            setInterests([
                              ...interests,
                              { id: item.id, name: item.name },
                            ]);
                          }
                          setInterestSuggestions([]);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs rounded-lg flex items-center justify-between gap-2 ${
                          isSelected
                            ? "bg-purple-100/50 text-purple-800 cursor-default"
                            : "hover:bg-purple-50 text-gray-700"
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="font-semibold block truncate">
                            {item.name}
                          </span>
                          {item.type && (
                            <span className="text-[10px] text-gray-400">
                              {item.type.replace("adinterest", t("Interesse"))}
                            </span>
                          )}
                        </span>
                        {isSelected ? (
                          <CheckCircle className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                        ) : (
                          <Plus className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {!searchingInterests &&
                interestSuggestions.length === 0 &&
                interestQuery.length >= 3 &&
                (interestSearchError ? (
                  <div className="px-3 py-2 text-[11px] text-red-600 font-semibold">
                    {t("Falha ao buscar interesses. Tente novamente.")}
                  </div>
                ) : (
                  <div className="px-3 py-2 text-[11px] text-gray-400 italic">
                    {t('Nenhum resultado para "{query}"', {
                      query: interestQuery,
                    })}
                  </div>
                ))}

              {/* Selected Interests Chips */}
              <div className="flex-1 flex flex-wrap content-start gap-1.5 min-h-[32px] pt-1">
                {interests.map((item) => (
                  <span
                    key={item.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-[10px] font-bold border border-purple-200/60 shadow-xs"
                  >
                    {item.name}
                    <button
                      type="button"
                      onClick={() =>
                        setInterests(interests.filter((i) => i.id !== item.id))
                      }
                      className="hover:text-red-500"
                      aria-label={t("Remover {name}", { name: item.name })}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {interests.length === 0 && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400 font-medium">
                    <Search className="w-3 h-3 text-purple-200 shrink-0" />
                    {t("Nenhum interesse adicionado (público amplo)")}
                  </span>
                )}
              </div>
            </div>
          ) : platform === "linkedin" ? (
            <>
              <div className="p-3.5 bg-white rounded-xl border border-[#0077b5]/20 space-y-2.5 shadow-xs">
                <div className="flex items-center gap-2 font-bold text-[#0077b5] text-xs uppercase tracking-tight border-b pb-2 border-[#0077b5]/10">
                  <UserSearch className="w-3.5 h-3.5" />
                  {t("Critérios B2B LinkedIn")}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase text-gray-500">
                      {t("Localidade (LinkedIn)")}
                    </label>
                    <LinkedInTargetingSearch
                      facet="locations"
                      placeholder={t("Ex: Brasil, São Paulo...")}
                      selectedItems={customLocations
                        .filter((l) => l.urn)
                        .map((l) => ({ urn: l.urn, name: l.name }))}
                      onAdd={(item) =>
                        setCustomLocations([
                          ...customLocations,
                          {
                            urn: item.urn,
                            name: item.name,
                            latitude: "0",
                            longitude: "0",
                            radius: 5,
                          },
                        ])
                      }
                      onRemove={(urn) =>
                        setCustomLocations(
                          customLocations.filter((l) => l.urn !== urn),
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase text-gray-500">
                      {t("Cargos")}
                    </label>
                    <LinkedInTargetingSearch
                      facet="jobTitles"
                      placeholder={t("Ex: Diretor, Gerente...")}
                      selectedItems={linkedinJobTitles}
                      onAdd={(item) =>
                        setLinkedinJobTitles([...linkedinJobTitles, item])
                      }
                      onRemove={(urn) =>
                        setLinkedinJobTitles(
                          linkedinJobTitles.filter((i) => i.urn !== urn),
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase text-gray-500">
                      {t("Funções / Departamentos")}
                    </label>
                    <LinkedInTargetingSearch
                      facet="jobFunctions"
                      placeholder={t("Ex: Marketing, Vendas, Engenharia...")}
                      selectedItems={linkedinJobFunctions}
                      onAdd={(item) =>
                        setLinkedinJobFunctions([...linkedinJobFunctions, item])
                      }
                      onRemove={(urn) =>
                        setLinkedinJobFunctions(
                          linkedinJobFunctions.filter((i) => i.urn !== urn),
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase text-gray-500">
                      {t("Setores")}
                    </label>
                    <LinkedInTargetingSearch
                      facet="industries"
                      placeholder={t("Ex: Software, Varejo...")}
                      selectedItems={linkedinIndustries}
                      onAdd={(item) =>
                        setLinkedinIndustries([...linkedinIndustries, item])
                      }
                      onRemove={(urn) =>
                        setLinkedinIndustries(
                          linkedinIndustries.filter((i) => i.urn !== urn),
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase text-gray-500">
                      {t("Tamanho da Empresa")}
                    </label>
                    <LinkedInTargetingSearch
                      facet="companySizes"
                      placeholder={t("Ex: 1-10, 11-50, 51-200...")}
                      selectedItems={linkedinCompanySizes}
                      onAdd={(item) =>
                        setLinkedinCompanySizes([...linkedinCompanySizes, item])
                      }
                      onRemove={(urn) =>
                        setLinkedinCompanySizes(
                          linkedinCompanySizes.filter((i) => i.urn !== urn),
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase text-gray-500">
                      {t("Senioridade")}
                    </label>
                    <LinkedInTargetingSearch
                      facet="seniorities"
                      placeholder={t("Ex: Sênior, VP...")}
                      selectedItems={linkedinSeniorities}
                      onAdd={(item) =>
                        setLinkedinSeniorities([...linkedinSeniorities, item])
                      }
                      onRemove={(urn) =>
                        setLinkedinSeniorities(
                          linkedinSeniorities.filter((i) => i.urn !== urn),
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase text-gray-500">
                      {t("Habilidades")}
                    </label>
                    <LinkedInTargetingSearch
                      facet="skills"
                      placeholder={t("Ex: Vendas, Gestão...")}
                      selectedItems={linkedinSkills}
                      onAdd={(item) =>
                        setLinkedinSkills([...linkedinSkills, item])
                      }
                      onRemove={(urn) =>
                        setLinkedinSkills(
                          linkedinSkills.filter((i) => i.urn !== urn),
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              {/* LinkedIn Ad Account Selector */}
              <div className="p-3.5 bg-white rounded-xl border border-[#0077b5]/20 space-y-2.5 shadow-xs">
                <div className="flex items-center gap-2 font-bold text-[#0077b5] text-xs uppercase tracking-tight border-b pb-2 border-[#0077b5]/10">
                  <Target className="w-3.5 h-3.5" />
                  {t("Conta de Anúncios LinkedIn")}
                </div>
                <Select
                  label={t("Selecione a Conta para Veiculação")}
                  value={selectedLinkedinAccount}
                  onChange={(val) => setSelectedLinkedinAccount(val as string)}
                  options={linkedinAccounts.map((acc) => ({
                    value: acc.id,
                    label: `${acc.name} (${acc.currency})`,
                  }))}
                  size="sm"
                  className="text-xs rounded-xl border-[#0077b5]/30"
                  isLoading={loadingLinkedinAccounts}
                />
                {linkedinAccounts.length === 0 && !loadingLinkedinAccounts && (
                  <div className="p-2.5 bg-amber-50 rounded-xl border border-amber-100 flex items-center gap-2.5">
                    <div className="w-1.5 h-1.5 bg-amber-500 rounded-full shrink-0" />
                    <p className="text-[10px] font-bold text-amber-800">
                      {t(
                        "Nenhuma conta encontrada. Verifique sua conexão LinkedIn.",
                      )}
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col p-3.5 bg-white rounded-xl border border-blue-100/80 space-y-2.5 shadow-xs">
              <div className="flex items-center justify-between border-b pb-2 border-blue-50">
                <div className="flex items-center gap-2 font-bold text-blue-700 text-xs uppercase tracking-tight">
                  <Search className="w-3.5 h-3.5" />
                  {t("Palavras-chave Google")}
                </div>
                <button
                  type="button"
                  onClick={optimizeKeywordsWithTrends}
                  disabled={trendsLoading}
                  className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 rounded-lg text-[10px] font-bold uppercase hover:bg-blue-100 transition-all disabled:opacity-50 border border-blue-200/60"
                >
                  <Zap
                    className={`w-3 h-3 ${trendsLoading ? "animate-pulse" : ""}`}
                  />
                  {trendsLoading ? t("Otimizando...") : t("Otimizar c/ Trends")}
                </button>
              </div>

              <div className="relative">
                <Plus className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-600" />
                <input
                  placeholder={t("Digite a palavra-chave e pressione Enter...")}
                  className="w-full h-10 pl-10 pr-4 bg-blue-50/30 border border-blue-200 rounded-xl text-xs outline-hidden focus:border-blue-600 focus:ring-2 focus:ring-blue-500/20 transition-all placeholder:text-gray-400 font-medium"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const val = (e.target as HTMLInputElement).value.trim();
                      if (val) {
                        addKeyword(val);
                        (e.target as HTMLInputElement).value = "";
                      }
                    }
                  }}
                />
              </div>

              <div className="flex-1 flex flex-wrap content-start gap-1.5 min-h-[32px] pt-1">
                {keywords.map((kw, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-[10px] font-bold border border-blue-200/60 shadow-xs"
                  >
                    {kw}
                    <button
                      type="button"
                      onClick={() => removeKeyword(i)}
                      className="hover:text-red-500"
                      aria-label={t("Remover {name}", { name: kw })}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {keywords.length === 0 && (
                  <span className="text-[11px] text-gray-400 italic">
                    {t("Nenhuma palavra-chave incluída.")}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CRM & Lookalike Audiences Section */}
      <div className="pt-3 border-t border-purple-100 space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold text-gray-700 uppercase tracking-tight">
            {t("Públicos Avançados de CRM / Remarketing")}
          </span>
        </div>

        {platform === "meta" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {/* Lookalike Toggle */}
            <div
              className={`p-3.5 rounded-xl border transition-all ${
                useMetaLookalike
                  ? "border-purple-600 bg-purple-50/40 shadow-xs"
                  : "border-gray-200 bg-gray-50/50 hover:bg-white"
              }`}
            >
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setUseMetaLookalike(!useMetaLookalike)}
              >
                <div className="flex items-center gap-2">
                  <Users
                    className={`w-4 h-4 ${useMetaLookalike ? "text-purple-600" : "text-gray-400"}`}
                  />
                  <div>
                    <div className="text-xs font-bold text-gray-900">
                      {t("Público Lookalike (Semelhante)")}
                    </div>
                    <div className="text-[10px] text-gray-500 font-medium">
                      {t("Pessoas semelhantes aos clientes do CRM")}
                    </div>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={useMetaLookalike}
                  readOnly
                  className="accent-purple-600 w-4 h-4 pointer-events-none"
                />
              </div>

              {useMetaLookalike && (
                <div className="mt-2.5 pt-2.5 border-t border-purple-100 space-y-2.5 animate-in fade-in duration-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Select
                      label={t("Origem")}
                      hint={t(
                        "De qual base o Meta vai construir o público semelhante: 'Leads Virtus' usa seus leads qualificados no CRM; 'Pixel Meta' usa visitantes rastreados pelo Pixel no seu site.",
                      )}
                      value={metaLookalikeSource}
                      onChange={(v) => setMetaLookalikeSource(v as any)}
                      options={[
                        {
                          value: "b2c_qualified_leads",
                          label: t("Leads Virtus"),
                        },
                        {
                          value: "pixel_event",
                          label: t("Pixel Meta"),
                        },
                      ]}
                      size="sm"
                      className="text-xs rounded-xl"
                    />
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-gray-500">
                        {t("Precisão")}
                      </label>
                      <div className="flex gap-1">
                        {["1", "2", "5"].map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setMetaLookalikePercent(p as any)}
                            className={`flex-1 h-10 rounded-xl text-xs font-bold transition-all ${
                              metaLookalikePercent === p
                                ? "bg-purple-600 text-white shadow-xs"
                                : "bg-white text-gray-600 border border-gray-200"
                            }`}
                          >
                            {p}%
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={createMetaLookalike}
                    isLoading={creatingLookalike}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white h-10 text-xs font-bold rounded-xl shadow-xs"
                  >
                    {t("Sincronizar Lookalike")}
                  </Button>
                </div>
              )}
            </div>

            {/* Custom Audience Toggle */}
            <div
              className={`p-3.5 rounded-xl border transition-all ${
                useMetaCustomAudience
                  ? "border-purple-600 bg-purple-50/40 shadow-xs"
                  : "border-gray-200 bg-gray-50/50 hover:bg-white"
              }`}
            >
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setUseMetaCustomAudience(!useMetaCustomAudience)}
              >
                <div className="flex items-center gap-2">
                  <UserSearch
                    className={`w-4 h-4 ${useMetaCustomAudience ? "text-purple-600" : "text-gray-400"}`}
                  />
                  <div>
                    <div className="text-xs font-bold text-gray-900">
                      {t("Público Personalizado (Lista Direta)")}
                    </div>
                    <div className="text-[10px] text-gray-500 font-medium">
                      {t("Remarketing para contatos já capturados")}
                    </div>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={useMetaCustomAudience}
                  readOnly
                  className="accent-purple-600 w-4 h-4 pointer-events-none"
                />
              </div>

              {useMetaCustomAudience && (
                <div className="mt-2.5 pt-2.5 border-t border-purple-100 space-y-2.5 animate-in fade-in duration-200">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold text-gray-500">
                      {t("Públicos Existentes")}
                    </span>
                    <div className="flex items-center gap-2.5">
                      <a
                        href="https://business.facebook.com/ads/manage/customaudiences/tos/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[10px] text-blue-600 font-bold hover:underline"
                      >
                        {t("Assinar Termos da Meta")}
                      </a>
                      <button
                        type="button"
                        onClick={fetchMetaCustomAudiences}
                        disabled={fetchingCustomAudiences}
                        className="text-[10px] text-purple-600 font-bold hover:underline flex items-center gap-1"
                      >
                        <RefreshCw
                          className={`w-2.5 h-2.5 ${fetchingCustomAudiences ? "animate-spin" : ""}`}
                        />
                        {t("Atualizar")}
                      </button>
                    </div>
                  </div>
                  {fetchingCustomAudiences ? (
                    <div className="flex items-center gap-2 p-2.5 border border-purple-100 rounded-xl bg-white text-[11px] text-gray-500">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-500" />
                      {t("Carregando seus públicos da Meta...")}
                    </div>
                  ) : metaCustomAudiencesList.length > 0 ? (
                    <Select
                      value={metaCustomAudienceId}
                      onChange={(v) => {
                        setMetaCustomAudienceId(String(v));
                        const aud = metaCustomAudiencesList.find(
                          (a) => String(a.id) === String(v),
                        );
                        if (aud) setMetaCustomAudienceName(aud.name);
                      }}
                      options={metaCustomAudiencesList.map((a) => ({
                        value: a.id,
                        label: `${a.name} (${a.approximate_count > 0 ? a.approximate_count : t("N/A")})`,
                      }))}
                      size="sm"
                      className="text-xs rounded-xl"
                    />
                  ) : (
                    <div className="text-[10px] text-gray-500">
                      {t(
                        "Nenhum Público Personalizado encontrado. Crie um no menu Leads ou através do botão abaixo.",
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Input
                        value={metaCustomAudienceName}
                        onChange={(e) =>
                          setMetaCustomAudienceName(e.target.value)
                        }
                        placeholder={t("Nome do público...")}
                        inputSize="sm"
                        className="text-xs rounded-xl"
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={createMetaCustomAudience}
                      isLoading={creatingCustomAudience}
                      className="bg-purple-600 hover:bg-purple-700 text-white h-10 text-xs font-bold rounded-xl px-4 whitespace-nowrap shadow-xs"
                    >
                      {t("Criar")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : platform === "linkedin" ? (
          <div className="p-3.5 bg-blue-50/30 rounded-xl border border-[#0077b5]/20 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[#0077b5]" />
                <div>
                  <span className="text-xs font-bold text-gray-900">
                    {t("Públicos Combinados LinkedIn (Matched Audiences)")}
                  </span>
                  <p className="text-[10px] text-gray-500">
                    {t(
                      "Listas de empresas, contatos e públicos de retargeting",
                    )}
                  </p>
                </div>
              </div>
            </div>
            {loadingAudiences ? (
              <div className="flex items-center gap-2 p-2.5 border border-[#0077b5]/20 rounded-xl bg-white text-[11px] text-gray-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#0077b5]" />
                {t("Carregando audiências...")}
              </div>
            ) : availableAudiences.length > 0 ? (
              <div className="space-y-2">
                <Select
                  label={t("Selecionar Público da Conta")}
                  value=""
                  onChange={(val) => {
                    if (!val) return;
                    const aud = availableAudiences.find(
                      (a) => a.urn === String(val),
                    );
                    if (aud) {
                      if (
                        !linkedinMatchedAudiences.find((m) => m.urn === aud.urn)
                      ) {
                        setLinkedinMatchedAudiences([
                          ...linkedinMatchedAudiences,
                          { urn: aud.urn, name: aud.name },
                        ]);
                      }
                    }
                  }}
                  options={[
                    { value: "", label: t("Selecione um público...") },
                    ...availableAudiences
                      .filter(
                        (a) =>
                          !linkedinMatchedAudiences.find(
                            (m) => m.urn === a.urn,
                          ),
                      )
                      .map((a) => ({
                        value: a.urn,
                        label: `${a.name} (${a.type || t("Custom")})`,
                      })),
                  ]}
                  size="sm"
                  className="text-xs rounded-xl border-[#0077b5]/30"
                />
                {linkedinMatchedAudiences.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {linkedinMatchedAudiences.map((aud) => (
                      <span
                        key={aud.urn}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#0077b5]/10 text-[#0077b5] rounded-lg text-[10px] font-bold border border-[#0077b5]/20 shadow-xs"
                      >
                        {aud.name}
                        <button
                          type="button"
                          onClick={() =>
                            setLinkedinMatchedAudiences(
                              linkedinMatchedAudiences.filter(
                                (a) => a.urn !== aud.urn,
                              ),
                            )
                          }
                          className="hover:text-red-500"
                          aria-label={t("Remover {name}", { name: aud.name })}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : audiencesError ? (
              <p className="text-[10px] text-red-600 font-semibold flex items-center gap-1.5">
                {t("Falha ao carregar públicos. Tente novamente.")}
                <button
                  type="button"
                  onClick={fetchAvailableAudiences}
                  className="underline hover:no-underline"
                >
                  {t("Recarregar")}
                </button>
              </p>
            ) : (
              <p className="text-[10px] text-gray-500 italic">
                {selectedLinkedinAccount
                  ? t(
                      "Nenhum público de retargeting encontrado para esta conta.",
                    )
                  : t("Selecione uma conta do LinkedIn para listar públicos.")}
              </p>
            )}
          </div>
        ) : (
          <div
            className={`p-3.5 rounded-xl border transition-all ${
              useGoogleCustomerMatch
                ? "border-blue-600 bg-blue-50/40 shadow-xs"
                : "border-gray-200 bg-gray-50/50 hover:bg-white"
            }`}
          >
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setUseGoogleCustomerMatch(!useGoogleCustomerMatch)}
            >
              <div className="flex items-center gap-2">
                <Smartphone
                  className={`w-4 h-4 ${useGoogleCustomerMatch ? "text-blue-600" : "text-gray-400"}`}
                />
                <div>
                  <div className="text-xs font-bold text-gray-900">
                    {t("Google Customer Match")}
                  </div>
                  <div className="text-[10px] text-gray-500 font-medium">
                    {t("Sincronização de leads qualificados com o Google Ads")}
                  </div>
                </div>
              </div>
              <input
                type="checkbox"
                checked={useGoogleCustomerMatch}
                readOnly
                className="accent-blue-600 w-4 h-4 pointer-events-none"
              />
            </div>

            {useGoogleCustomerMatch && (
              <div className="mt-2.5 pt-2.5 border-t border-blue-100 space-y-2.5 animate-in fade-in duration-200">
                <Select
                  label={t("Origem")}
                  hint={t(
                    "Qual base de contatos será sincronizada com o Google para montar o público de Customer Match.",
                  )}
                  value={googleCustomerMatchSource}
                  onChange={(v) => setGoogleCustomerMatchSource(v as any)}
                  options={[
                    {
                      value: "b2c_qualified_leads",
                      label: t("Leads Qualificados (B2C)"),
                    },
                    { value: "b2b_customers", label: t("Clientes B2B") },
                    { value: "crm_upload", label: t("Upload de CRM") },
                  ]}
                  size="sm"
                  className="text-xs rounded-xl"
                />
                <Button
                  size="sm"
                  onClick={createGoogleCustomerMatch}
                  isLoading={creatingCustomerMatch}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10 text-xs font-bold rounded-xl shadow-xs"
                >
                  {t("Sincronizar")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudienceProfile;
